import '../stylesheets/default-wayke-admin.scss';

import { ApolloClient, ApolloLink, concat, HttpLink, InMemoryCache } from '@apollo/client';
import { ApolloProvider } from '@apollo/client/react';
import { QueryClientProvider } from '@tanstack/react-query';
import React, { Suspense } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import App from './components/app';
import { SpinnerBlock } from './components/spinner';
import queryClient from './data/queryClient';
import { get as getSetting } from './helpers/settings';
import Routes from './routes';
import configureStore from './shared/store';
import GlobalStyle from './styled-components/layout/global-style';
import theme from './styled-components/layout/theme';

const url = (groupId?: string) => {
  const _groupId = window.location.pathname.split('/')?.[2] || groupId;
  return `${getSetting('processFlowUri')}/graphql?groupId=${_groupId}`;
};

const clients = {
  processFlow: new HttpLink({
    uri: (s) => {
      return url(s.variables.groupId);
    },
  }),
  virtualVehicle: new HttpLink({
    uri: `${getSetting('virtualVehicleUri')}/graphql`,
  }),
};

const link = ApolloLink.split(
  (operation) => operation.getContext().clientName === 'virtualVehicle',
  clients.virtualVehicle,
  clients.processFlow,
);

const authMiddleware = new ApolloLink((operation, forward) => {
  // add the authorization to the headers
  const token = localStorage.getItem('token');
  operation.setContext({
    headers: {
      authorization: token ? `Bearer ${token}` : '',
    },
  });
  return forward(operation);
});

const client = new ApolloClient({
  devtools: { enabled: true },
  link: concat(authMiddleware, link),
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: {
      notifyOnNetworkStatusChange: true,
    },
  },
});

const store = configureStore();

window.addEventListener('load', () => {
  const element = document.querySelector('#application-node');
  if (!element) throw 'Missing root element';
  const root = createRoot(element);
  root.render(
    <Provider store={store}>
      <ApolloProvider client={client}>
        <QueryClientProvider client={queryClient}>
          <DndProvider backend={HTML5Backend}>
            <ThemeProvider theme={theme}>
              <GlobalStyle />
              <BrowserRouter>
                <App>
                  <Suspense fallback={<SpinnerBlock />}>
                    <Routes />
                  </Suspense>
                </App>
              </BrowserRouter>
            </ThemeProvider>
          </DndProvider>
        </QueryClientProvider>
      </ApolloProvider>
    </Provider>,
  );
});
